/* eslint-disable jsx-a11y/anchor-is-valid */
import { useIonToast } from '@ionic/react';
import { BlockThresholds, SupportedSendMailServices, aiServices, objectEquals, timeZones } from 'model';
import { useHistory } from 'react-router';
// eslint-disable-next-line css-modules/no-unused-class

import {
	useAddSiteBannerDarkMutation,
	useAddSiteBannerLightMutation,
	useAddSiteIconDarkMutation,
	useAddSiteIconLightMutation,
	useGetSiteSettingsQuery,
	useUpdateSiteSettingsMutation
} from '../api/SiteSettingsAPI';
import { FormCheckbox } from '../components/Forms/FormCheckbox';
import { FormImageUpload } from '../components/Forms/FormImageUpload';
import { FormInput } from '../components/Forms/FormInput';
import { FormSelectBox } from '../components/Forms/FormSelectBox';
import { FormTextarea } from '../components/Forms/FormTextarea';
import { Header } from '../components/Header';
import { IconButton } from '../components/IconButton';
import { IconCheck, IconClose } from '../components/QuirkIcons';
import { QuirkIonContent } from '../components/QuirkIonContent';
import { QuirkIonPage } from '../components/QuirkIonPage';
import { siteSettingsRules } from '../formConfigs/SiteSettingsConfig';
import { useFormValues } from '../hooks/useFormValues';
import { useLoading } from '../hooks/useQuirkLoading';

// eslint-disable-next-line css-modules/no-unused-class
import formStyles from '../components/Forms/FormFields.module.css';
import { useLicenseFeature } from '../hooks/useLiscenseFeature.js';

export const EditSiteSettings = () => {
	const paymentsEnabled = useLicenseFeature('payments');
	const emailIntegrationEnabled = useLicenseFeature('emailIntegration');
	const aiModerationEnabled = useLicenseFeature('aiModeration');
	const customDomainEnabled = useLicenseFeature('customDomain');
	const [showToast] = useIonToast();
	const { data, isLoading, isFetching } = useGetSiteSettingsQuery();
	useLoading({ isLoading: isLoading || isFetching });
	const history = useHistory();
	const [updateSiteSettings] = useUpdateSiteSettingsMutation();
	const {
		setValues,
		formSuccess,
		validateForm,
		formValues,
		stateDataObject: editedSiteSettings
	} = useFormValues(siteSettingsRules, data, objectEquals);

	const onSubmit = async () => {
		if (validateForm() && formValues && data && editedSiteSettings) {
			const result = await updateSiteSettings(editedSiteSettings);
			if (!('error' in result)) {
				showToast({
					color: 'success',
					message: 'Site Settings have been updated',
					position: 'top',
					duration: 1500
				});
				history.go(-1);
			} else {
				if ('status' in result.error) {
					const status = result.error.status;
					let errorMessage = 'Error updating site settings.';
					if (status === 403) {
						errorMessage = 'You lack permissions to update site settings.';
					}

					showToast({
						color: 'danger',
						message: errorMessage,
						position: 'top',
						duration: 1500
					});
				}
			}
		} else {
			showToast({
				color: 'danger',
				message: 'Please complete all highlighted fields.',
				position: 'top',
				duration: 1500
			});
		}
	};
	return (
		<QuirkIonPage name="Edit Site Settings">
			<>
				<Header
					title="Edit Site Settings"
					left={
						<IconButton
							icon={<IconClose />}
							onClick={() => {
								history.go(-1);
							}}
						/>
					}
					right={
						<IconButton
							label="Done"
							fill="solid"
							disabled={!formSuccess}
							icon={<IconCheck />}
							onClick={() => onSubmit()}
						/>
					}
				/>
				<QuirkIonContent padding>
					<div className={formStyles.fieldsContainer}>
						<FormInput id="siteName" setValues={setValues} field={formValues?.siteName} label="Name" />
						<FormInput
							id="legalName"
							setValues={setValues}
							field={formValues?.legalName}
							label="Legal Name"
						/>
						<FormTextarea
							id="description"
							type="text"
							setValues={setValues}
							field={formValues?.description}
							label="Description"
						/>
						{customDomainEnabled && (
							<>
								<FormInput
									id="serverUrl"
									setValues={setValues}
									field={formValues?.serverUrl}
									label="Full URL of the website"
								/>
								<FormInput
									id="serverPort"
									type="number"
									setValues={setValues}
									field={formValues?.serverPort}
									label="Port number of the website, usually 0 for default"
								/>
							</>
						)}
						<FormSelectBox
							id="timezone"
							setValues={setValues}
							field={formValues?.timezone}
							label="Timezone"
							options={timeZones.reduce((obj, value) => {
								obj[value as string] = value as string;
								return obj;
							}, {} as { [key: string]: string })}
						/>
						<FormInput id="language" setValues={setValues} field={formValues?.language} label="Language" />
						<FormInput id="currency" setValues={setValues} field={formValues?.currency} label="Currency" />
						<FormCheckbox
							id="allowAnonymous"
							setValues={setValues}
							field={formValues?.allowAnonymous}
							label="Allow anonymous users to view the site"
						/>
						<FormInput
							id="feedRandomnessFactor"
							type="number"
							setValues={setValues}
							field={formValues?.feedRandomnessFactor}
							label="Feed Randomness Factor (0-10)"
							min="0"
							max="10"
						/>
						<FormInput
							id="feedPromotionDensity"
							type="number"
							setValues={setValues}
							field={formValues?.feedPromotionDensity}
							label="Feed Promotion Density"
							min="0"
							max="100"
						/>
						<FormImageUpload
							url="api/siteSettings/image/"
							fieldName="lightIcon"
							document={data}
							setValues={setValues}
							label="Light Theme Site Icon"
							addImage={useAddSiteIconLightMutation}
						/>
						<FormImageUpload
							url="api/siteSettings/image/"
							fieldName="darkIcon"
							document={data}
							setValues={setValues}
							label="Dark Theme Site Icon"
							addImage={useAddSiteIconDarkMutation}
						/>
						<FormImageUpload
							url="api/siteSettings/image/"
							fieldName="lightBanner"
							document={data}
							setValues={setValues}
							label="Light Theme Banner image"
							addImage={useAddSiteBannerLightMutation}
						/>
						<FormImageUpload
							url="api/siteSettings/image/"
							fieldName="darkBanner"
							document={data}
							setValues={setValues}
							label="Dark Theme Banner Image"
							addImage={useAddSiteBannerDarkMutation}
						/>
						{emailIntegrationEnabled && (
							<>
								<FormInput
									id="adminEmail"
									setValues={setValues}
									field={formValues?.adminEmail}
									label="Administrative Email address"
								/>
								<FormSelectBox
									id="sendMailServiceType"
									setValues={setValues}
									field={formValues?.sendMailServiceType}
									label="Email Service Provider"
									options={SupportedSendMailServices.reduce(
										(accum, service) => ({ ...accum, [service]: service }),
										{
											'': 'None'
										}
									)}
								/>
								{formValues?.sendMailServiceType?.value && (
									<>
										<FormInput
											id="sendMailUser"
											setValues={setValues}
											field={formValues?.sendMailUser}
											label="Email service provider username"
										/>
										<FormInput
											id="sendMailPassword"
											type="password"
											setValues={setValues}
											field={formValues?.sendMailPassword}
											label="Email service provider password"
										/>
									</>
								)}
							</>
						)}
						<FormTextarea
							id="welcomeEmail"
							type="text"
							setValues={setValues}
							field={formValues?.welcomeEmail}
							label="Welcome Email"
						/>
						<FormTextarea
							id="gettingStarted"
							type="text"
							setValues={setValues}
							field={formValues?.gettingStarted}
							label="Getting started message"
						/>
						<FormInput
							id="termsOfServiceVersion"
							setValues={setValues}
							type="number"
							field={formValues?.termsOfServiceVersion}
							label="Terms of Service Version"
						/>
						<FormTextarea
							id="termsOfService"
							type="text"
							long
							setValues={setValues}
							field={formValues?.termsOfService}
							label="Terms of Service"
						/>
						{paymentsEnabled && (
							<>
								<FormInput
									id="stripeAPIKey"
									setValues={setValues}
									field={formValues?.stripeAPIKey}
									label="Stripe API Key"
								/>
								<FormInput
									id="stripeTokenProductKey"
									setValues={setValues}
									field={formValues?.stripeTokenProductKey}
									label="Stripe Token Product ID"
								/>

								<FormInput
									id="stripeSubscriptionProductKey"
									setValues={setValues}
									field={formValues?.stripeSubscriptionProductKey}
									label="Stripe Subscription Product ID"
								/>
								<FormInput
									type="number"
									id="subscriptionCost"
									setValues={setValues}
									field={formValues?.subscriptionCost}
									label="Cost of subscription"
								/>
								<FormInput
									type="number"
									id="subscriptionDuration"
									setValues={setValues}
									field={formValues?.subscriptionDuration}
									label="Subscription duration in months"
								/>
							</>
						)}
						<FormSelectBox
							id="autoBlockThreshold"
							setValues={setValues}
							field={formValues?.autoBlockThreshold}
							label="Auto-Block Threshold"
							options={BlockThresholds.reduce((obj, value) => {
								obj[value as string] = value as string;
								return obj;
							}, {} as { [key: string]: string })}
						/>
						{aiModerationEnabled && (
							<>
								<FormInput
									id="aiServiceKey"
									setValues={setValues}
									field={formValues?.aiServiceKey}
									label="AI Service Key"
								/>
								<FormSelectBox
									id="aiService"
									setValues={setValues}
									field={formValues?.aiService}
									label="AI Service"
									options={aiServices.reduce((obj, value) => {
										obj[value as string] = value as string;
										return obj;
									}, {} as { [key: string]: string })}
								/>
								<FormCheckbox
									id="moderatePosts"
									setValues={setValues}
									field={formValues?.moderatePosts}
									label="AI moderate posts"
								/>
								<FormCheckbox
									id="moderateComments"
									setValues={setValues}
									field={formValues?.moderateComments}
									label="AI moderate comments"
								/>
								<FormTextarea
									id="level1Prompt"
									type="text"
									setValues={setValues}
									field={formValues?.level1Prompt}
									label="Level 1 Prompt"
								/>
								<FormTextarea
									id="level2Prompt"
									type="text"
									setValues={setValues}
									field={formValues?.level2Prompt}
									label="Level 2 Prompt"
								/>
								<FormTextarea
									id="level3Prompt"
									type="text"
									setValues={setValues}
									field={formValues?.level3Prompt}
									label="Level 3 Prompt (Content is Reported)"
								/>
							</>
						)}
					</div>
				</QuirkIonContent>
			</>
		</QuirkIonPage>
	);
};
